/**
 * Réserve d'objets réutilisables (E§3).
 *
 * Obligatoire pour les particules, les pièces volantes et les textes flottants. Sans
 * elle, une volée de pièces alloue des dizaines d'objets par seconde, et le ramasse-miettes
 * finit par interrompre le rendu — ces micro-blocages sont exactement ce que le joueur
 * perçoit comme « ça saccade », sans jamais pouvoir le décrire.
 *
 * Volontairement sans dépendance : la réserve ne sait pas ce qu'elle stocke. Elle sert
 * aussi bien des sprites PixiJS que des structures de particules.
 */

export interface PoolOptions<T> {
  /** Crée un objet neuf quand la réserve est vide. */
  readonly create: () => T;
  /** Remet un objet dans un état neutre avant de le rendre disponible. */
  readonly reset: (item: T) => void;
  /** Objets pré-alloués au démarrage, pour éviter le coût au premier usage. */
  readonly initialSize?: number;
  /**
   * Plafond dur d'objets vivants. Au-delà, `acquire()` rend `null` plutôt que de
   * laisser une fuite grossir sans fin : mieux vaut une particule manquante qu'une
   * chute de fréquence d'images (E§3).
   */
  readonly maxSize?: number;
}

export class Pool<T> {
  private readonly available: T[] = [];
  private readonly options: PoolOptions<T>;
  private live = 0;

  constructor(options: PoolOptions<T>) {
    this.options = options;

    for (let i = 0; i < (options.initialSize ?? 0); i++) {
      this.available.push(options.create());
    }
  }

  /** Objets actuellement empruntés. */
  get inUse(): number {
    return this.live;
  }

  /** Objets prêts à être empruntés sans allocation. */
  get idle(): number {
    return this.available.length;
  }

  /** Total détenu par la réserve, emprunté ou non. */
  get size(): number {
    return this.live + this.available.length;
  }

  /** Emprunte un objet, ou `null` si le plafond est atteint. */
  acquire(): T | null {
    const maxSize = this.options.maxSize;
    if (maxSize !== undefined && this.live >= maxSize) {
      return null;
    }

    const recycled = this.available.pop();
    const item = recycled ?? this.options.create();
    this.live++;
    return item;
  }

  /** Rend un objet. Le remettre deux fois est une erreur silencieuse évitée ici. */
  release(item: T): void {
    if (this.live === 0) {
      return;
    }
    this.options.reset(item);
    this.available.push(item);
    this.live--;
  }

  /** Vide la réserve des objets inutilisés. Les objets empruntés ne sont pas touchés. */
  trim(): void {
    this.available.length = 0;
  }
}
